import { action, computed, makeObservable, observable } from 'mobx'
import { Observable, Subscription, distinctUntilChanged } from 'rxjs'
import { AlertFactory } from 'alerts/Alert'
import BillingStore from 'billing/BillingStore'
import CouponStore from 'billing/CouponStore'
import BillingMethod from 'billing/models/BillingMethod'
import DocumentStore from 'documents/DocumentStore'
import DocumentTypeStore from 'documents/DocumentTypeStore'
import { DocumentDetailsRootViewModel } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import MemberStore from 'iam/MemberStore'
import Member from 'iam/models/Member'
import Provider from 'iam/models/Provider'
import { SearchableUser } from 'iam/models/SearchableUser'
import { UserDataType } from 'iam/models/UserDataType'
import Workspace from 'iam/models/Workspace'
import ProviderStore from 'iam/ProviderStore'
import RoleStore from 'iam/RoleStore'
import SearchableUserStore from 'iam/SearchableUserStore'
import { GetCurrentMember } from 'iam/SessionStore'
import SkillStore from 'iam/SkillStore'
import TeamStore from 'iam/TeamStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import UserDataStore from 'iam/UserDataStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import JobCreationDialogViewModel from 'jobs/creation/views/JobCreationDialog/JobCreationDialogViewModel'
import JobStore from 'jobs/JobStore'
import JobStatsStore from 'jobs/job-stats/JobStatsStore'
import { JobManagementViewModelFactory } from 'jobs/views/JobManagement/JobManagementViewModel'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import { Id } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'
import { AddUserToTeamViewModel } from '../AddUserToTeamDialog/AddUserToTeamViewModel'
import { AddUserToWorkspaceViewModel } from '../AddUserToWorkspaceDialog/AddUserToWorkspaceViewModel'
import { DocumentsTabViewModel } from '../DocumentsTab/DocumentsTabViewModel'
import { EditUserInformationDialogViewModel } from '../EditUserInformation/EditUserInformationDialogViewModel'
import { EditUserRoleDialogViewModel } from '../EditUserRoleDialog/EditUserRoleDialogViewModel'
import { JobsTabViewModel } from '../JobsTab/JobsTabViewModel'
import { ManageSkillsListViewModel } from '../ManageSkillsListDialog/ManageSkillsListViewModel'
import { ManageUserCapacityViewModel } from '../ManageUserCapacityDialog/ManageUserCapacityViewModel'
import { MembershipsTabViewModel } from '../MembershipsTab/MembershipsTabViewModel'
import { RemoveFromWorkspaceViewModel } from '../RemoveFromWorkspaceDialog/RemoveFromWorkspaceViewModel'
import { RestoreToWorkspaceViewModel } from '../RestoreToWorkspaceDialog/RestoreToWorkspaceViewModel'
import UserInfoItemListViewModel from '../UserInfoItemListDialog/UserInfoItemListViewModel'
import BillingProfile from '../../../billing/models/BillingProfile'
import AdminPayoutMethodStore, {
  PayoutMethodInfo,
} from 'payouts/admin/AdminPayoutMethodStore'

/**
 * List of tabs to be displayed in the user details view.
 */
export enum UserDetailsTab {
  Jobs = 'Jobs',
  Memberships = 'Memberships',
  Documents = 'Documents',
}

export class UserDetailsViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The currently selected user.
   */
  @observable
  user: SearchableUser = SearchableUser.none

  /**
   * Provider data for the user if user is a provider.
   */
  @observable
  provider: Provider = Provider.none

  /**
   * List of payment methods for the currently selected user.
   */
  @observable
  paymentMethods: BillingMethod[] = []

  /**
   * The payment method for the Pro, if any.
   */
  @observable
  payoutMethodInfo: PayoutMethodInfo = { status: 'Unknown' }

  /**
   * The currently selected tab.
   */
  @observable
  tab: UserDetailsTab = UserDetailsTab.Jobs

  /**
   * Number of active jobs for the currently selected user.
   */
  @observable
  activeJobs = 0

  /**
   * Number of total jobs for the currently selected user.
   */
  @observable
  totalJobs = 0

  /**
   * Whether the user is initialized.
   */
  @observable
  isUserInitialized = false

  /**
   * View model for the memberships tab.
   */
  membershipsTabVM = new MembershipsTabViewModel(
    () => this.user,
    (workspace: Workspace) => this.addUserToTeamVM.showForWorkspace(workspace),
    () => this.addUserToWorkspaceVM.show(),
    this.couponStore,
    this.jobSearchStore,
    this.teamMemberStore,
    (user: SearchableUser, workspace: Workspace) =>
      this.editUserRoleVM.showWithUser(user, workspace),
    (user: SearchableUser, workspace: Workspace) =>
      this.removeFromWorkspaceVM.showWithUser(user, workspace),
    (user: SearchableUser, workspace: Workspace) =>
      this.restoreToWorkspaceVM.showWithUser(user, workspace),
    (user: SearchableUser, workspace: Workspace) =>
      this.manageUserCapacityVM.showWithUser(user, workspace),
    (user: SearchableUser, workspace: Workspace) =>
      this.manageSkillListVM.showWithUser(user, workspace),
    this.memberStore,
    this.getCurrentMember,
    this.newAlert,
    this.logout,
    this.jobStatsStore,
  )

  jobsTabVM = new JobsTabViewModel(
    () => this.user.publicId,
    () => this.availableWorkspaces,
    this.jobManagementFactory('UserDetails'),
  )

  documentsTabVM = new DocumentsTabViewModel(
    () => this.user.publicId,
    () => this.user.memberships,
    () => this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships(),
    this.documentDetailsRootVM,
    this.documentStore,
    this.documentTypeStore,
    this.jobStore,
    this.jobSearchStore,
    this.currentMember$,
    this.getCurrentMember,
    this.filterStorageFactory,
    this.newAlert,
  )

  editUserRoleVM = new EditUserRoleDialogViewModel(
    this.roleStore,
    this.memberStore,
    this.newAlert,
  )

  removeFromWorkspaceVM = new RemoveFromWorkspaceViewModel(
    this.memberStore,
    this.newAlert,
    this.initialize.bind(this),
  )

  /**
   * View model for the add user to team dialog.
   */
  addUserToTeamVM = new AddUserToTeamViewModel(
    () => this.user,
    this.newAlert,
    this.teamStore,
    this.teamMemberStore,
    this.teamRoleStore,
    this.workspaceMembershipStore,
  )

  /**
   * View model for the add user to workspace dialog.
   */
  addUserToWorkspaceVM = new AddUserToWorkspaceViewModel(
    () => this.user,
    () => this.refreshMemberships(),
    this.newAlert,
    this.memberStore,
    this.roleStore,
    this.skillStore,
    this.teamStore,
    this.teamRoleStore,
    this.workspaceMembershipStore,
  )

  /**
   * Stream of changes to the user details view.
   */
  private userDetails$ = toStream(() => this.tab, true).pipe(
    distinctUntilChanged(),
    tapAction((tab) => {
      switch (tab) {
        case UserDetailsTab.Memberships:
          this.membershipsTabVM.activate()
          break
        case UserDetailsTab.Jobs:
          this.jobsTabVM.activate()
          break
        case UserDetailsTab.Documents:
          this.documentsTabVM.activate()
          break
      }
    }),
  )

  /**
   * Creates a new user info item list view model.
   */
  userInfoItemListVM = new UserInfoItemListViewModel()

  editUserInformationVM = new EditUserInformationDialogViewModel(
    this.newAlert,
    this.getCurrentMember,
    this.memberStore,
    this.userDataStore,
    this.initialize.bind(this),
  )

  manageUserCapacityVM = new ManageUserCapacityViewModel(
    this.memberStore,
    this.userDataStore,
    this.newAlert,
    this.getCurrentMember,
    this.initialize.bind(this),
  )

  restoreToWorkspaceVM = new RestoreToWorkspaceViewModel(
    this.memberStore,
    this.newAlert,
  )

  constructor(
    private currentMember$: Observable<Member>,
    private getCurrentMember: GetCurrentMember,
    private billingStore: BillingStore,
    private adminPayoutMethodStore: AdminPayoutMethodStore,
    private couponStore: CouponStore,
    private documentStore: DocumentStore,
    private documentTypeStore: DocumentTypeStore,
    private jobStore: JobStore,
    private jobSearchStore: JobSearchStore,
    private memberStore: MemberStore,
    private providerStore: ProviderStore,
    private roleStore: RoleStore,
    private searchableUserStore: SearchableUserStore,
    private skillStore: SkillStore,
    private teamStore: TeamStore,
    private teamMemberStore: TeamMemberStore,
    private teamRoleStore: TeamRoleStore,
    private userDataStore: UserDataStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private newAlert: AlertFactory,
    private canAccessUserManagement: () => boolean,
    private jobCreationDialogVM: JobCreationDialogViewModel,
    public manageSkillListVM: ManageSkillsListViewModel,
    private logout: () => Promise<void>,
    private jobManagementFactory: JobManagementViewModelFactory,
    private jobStatsStore: JobStatsStore,
    private documentDetailsRootVM: DocumentDetailsRootViewModel,
    private filterStorageFactory: FilterStorageFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  async activate() {
    await this.initialize()
    this.subscription = this.userDetails$.subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.reset()

    this.membershipsTabVM.deactivate()
    this.jobsTabVM.deactivate()
    this.documentsTabVM.deactivate()
  }

  @computed
  get isPlatformAdmin() {
    return this.getCurrentMember().user.platformAdmin
  }

  @computed
  get isUserDataTypePro() {
    return this.user.userDataType === UserDataType.Pro
  }

  @computed
  get availableWorkspacesToManageBilling() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.hasPermission('ADMIN_BILLING_MANAGE'))
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces available to the current user (i.e. the admin).
   */
  @computed
  get currentUserWorkspaces() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.hasPermission('ADMIN_USER_MODIFY'))
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * AND that the current user is a member of.
   */
  @computed
  get availableWorkspaces() {
    return this.user.memberships
      .map((mb) => mb.workspace)
      .filter((ws) => this.currentUserWorkspaces.includes(ws))
  }

  /**
   * The list of ACTIVE workspace memberships that are available to the currently selected user
   * AND that the current user is a member of.
   */
  @computed
  get availableWorkspaceMemberships() {
    return this.user.memberships.filter(
      (mb) =>
        !mb.member.inactive &&
        this.currentUserWorkspaces.includes(mb.workspace),
    )
  }

  /**
   * Whether the user should be allowed access to view user details.
   */
  @computed
  get canAccessUserDetails() {
    return this.canAccessUserManagement()
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * and that the current user can create a job for said user.
   */
  @computed
  get availableWorkspacesForJobCreation() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter(
        (m) =>
          m.member.hasPermission('ADMIN_JOB_CREATE') &&
          this.user.memberships.some(
            (um) => um.workspace.id === m.workspace.id,
          ),
      )
  }

  /**
   * Whether the current user can create jobs for the selected user.
   */
  @computed
  get canCreateJobForUser() {
    return this.availableWorkspacesForJobCreation.length > 0
  }

  /**
   * Whether the current user can view the payout method for the selected user.
   */
  @computed
  get canViewPayoutMethod() {
    return this.getCurrentMember().user.platformAdmin
  }

  /**
   * Set the currently selected user.
   */
  @action.bound
  setUser(user: SearchableUser) {
    this.user = user
  }

  /**
   * Set the provider data for the currently selected user.
   */
  @action.bound
  setProvider(provider: Provider) {
    this.provider = provider
  }

  /**
   * Set the list of payment methods for the currently selected user.
   */
  @action.bound
  setPaymentMethods(paymentMethods: BillingMethod[]) {
    this.paymentMethods = paymentMethods
  }

  /**
   * Set the currently selected tab.
   */
  @action.bound
  setTab(tab: UserDetailsTab) {
    this.tab = tab
  }

  /**
   * Set the number of active jobs for the currently selected user.
   */
  @action.bound
  setActiveJobs(activeJobs: number) {
    this.activeJobs = activeJobs
  }

  /**
   * Set the number of total jobs for the currently selected user.
   */
  @action.bound
  setTotalJobs(totalJobs: number) {
    this.totalJobs = totalJobs
  }

  /**
   * Set whether the user is initialized.
   */
  @action.bound
  setIsUserInitialized(isUserInitialized: boolean) {
    this.isUserInitialized = isUserInitialized
  }

  /**
   * Initializes the view model.
   */
  async initialize() {
    // Collect the user public id from the URL. Assume a length of 36 characters.
    const userPublicId = window.location.pathname
      .split('/users/')[1]
      .substring(0, 36)
    const user = await this.searchableUserStore
      .getUser(userPublicId, {
        memberships: true,
      })
      .then((user) => {
        this.setUser(user)
        this.setIsUserInitialized(true)
        return user
      })

    const jobTotals = await this.jobStatsStore.getJobTotals(userPublicId)
    this.setActiveJobs(jobTotals.actives)
    this.setTotalJobs(jobTotals.total)

    const billingProfiles = await Promise.all(
      this.availableWorkspaceMemberships.reduce((result, mb) => {
        if (
          this.availableWorkspacesToManageBilling.some(
            (ws) => ws.id === mb.workspace.id,
          )
        ) {
          result.push(
            this.billingStore.fetchBillingProfileForIndividual(
              mb.workspace.id,
              mb.member.user.publicId,
            ),
          )
        }

        return result
      }, [] as Promise<BillingProfile>[]),
    )

    if (billingProfiles.length > 0) {
      const defaultBillingMethods = billingProfiles
        .map((profile) => profile.billingMethods.find((bm) => bm.default))
        .filter((bm) => bm)

      // Undefined default billing methods have already been filtered out.
      this.setPaymentMethods(defaultBillingMethods as BillingMethod[])
    }

    if (user.userDataType === UserDataType.Pro) {
      const provider = this.providerStore.primeProvider({
        user_public_id: userPublicId,
      })

      if (provider) {
        this.setProvider(provider)
      }

      const payoutMethodInfo =
        await this.adminPayoutMethodStore.fetchPayoutMethodInfo(userPublicId)
      this.setPayoutMethodInfo(payoutMethodInfo)
    }
  }

  /**
   * Refresh the user's memberships.
   */
  async refreshMemberships() {
    if (!this.user.publicId) {
      return
    }

    const user = await this.searchableUserStore.getUser(
      Id.assert(this.user.publicId),
      {
        memberships: true,
      },
    )

    this.setUser(user)
  }

  /**
   * Creates job for user.
   */
  async createJob() {
    if (!this.user.publicId) {
      return
    }

    this.jobCreationDialogVM.open(
      undefined,
      new PrefilledDraftInput(undefined, undefined, undefined, [], {
        publicId: this.user.publicId,
        workspaceIds: this.availableWorkspacesForJobCreation.map((m) =>
          Id.assert(m.workspace.id),
        ),
      }),
    )
  }

  @action.bound
  setPayoutMethodInfo(payoutMethod: PayoutMethodInfo) {
    this.payoutMethodInfo = payoutMethod
  }

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    this.setIsUserInitialized(false)
    this.setUser(SearchableUser.none)
    this.setProvider(Provider.none)
    this.setPaymentMethods([])
    this.setTab(UserDetailsTab.Jobs)
    this.setActiveJobs(0)
    this.setTotalJobs(0)
    this.setPayoutMethodInfo({ status: 'Unknown' })
  }
}
